// One record field, rendered for the aside.
//
// It lives here rather than in a route because two pages now put the same fields
// in the same rail: the generic record page, and the client page that /e/org_*
// redirects to. The field def decides the control, so a kind that gains a field
// gains an editor on both pages without either of them being touched.

import { useState } from 'react'

import { optionsFor, type FieldDef } from '../lib/model'
import { fieldStr, type EntityRecord } from '../lib/reducer'
import { useStore } from '../lib/store'
import { EditableText, PillSelect } from './common'
import { Icon } from './Icon'
import { LinksField } from './Links'
import { Fact } from './Page'
import { RefPicker } from './RefPicker'

export function AsideField({
  def,
  rec,
  onSave,
}: {
  def: FieldDef
  rec: EntityRecord
  onSave: (v: string | number) => void
}) {
  const { config } = useStore()
  const value = fieldStr(rec, def.key)
  return (
    <Fact label={def.label}>
      {def.type === 'ref' && def.refKind ? (
        <RefPicker kind={def.refKind} value={value} onPick={onSave} onClear={() => onSave('')} />
      ) : def.type === 'select' && def.options ? (
        // A pill reading "set" says nothing about what it is or that it can be
        // pressed. The field's own placeholder says what an unset one MEANS — an
        // org nobody has marked is a client, which is the true default.
        <PillSelect
          options={optionsFor(def.options, config)}
          value={value}
          placeholder={def.placeholder ?? 'Set…'}
          onPick={onSave}
        />
      ) : def.type === 'links' ? (
        <LinksField value={value} onSave={onSave} />
      ) : def.type === 'url' && value.trim() ? (
        <UrlField value={value} onSave={onSave} />
      ) : (
        <EditableText
          value={value}
          placeholder={def.type === 'url' ? 'Add…' : def.placeholder}
          type={
            def.type === 'date'
              ? 'date'
              : def.type === 'number'
                ? 'number'
                : def.type === 'email'
                  ? 'email'
                  : 'text'
          }
          onSave={(v) => onSave(def.type === 'number' ? Number(v.replace(/[^0-9.]/g, '')) || 0 : v)}
          className="text-body"
          style={{ marginLeft: -8, ...(def.type === 'date' ? { fontFamily: 'var(--mono)' } : {}) }}
        />
      )}
    </Fact>
  )
}

function UrlField({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false)
  if (editing)
    return (
      <EditableText
        value={value}
        type="url"
        className="text-body"
        style={{ marginLeft: -8 }}
        onSave={(v) => {
          onSave(v)
          setEditing(false)
        }}
      />
    )
  const href = /^https?:\/\//.test(value) ? value : `https://${value}`
  return (
    <span className="inline-flex items-center gap-1.5">
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-body truncate hover:underline"
        style={{ color: 'var(--marine)' }}
      >
        {value}
      </a>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="opacity-40 hover:opacity-100"
        title="Edit"
      >
        <Icon name="Pencil" size={12} />
      </button>
    </span>
  )
}
